import type { ElementType, LayoutElement } from '../types';

export const ENTITY_NAME_PREFIX: Record<ElementType, string> = {
  'overhead-rail': 'Rail',
  'overhead-curve': 'Rail Curve',
  'cross-rail': 'Junction',
  'oht-vehicle': 'OHT',
  stocker: 'Stocker',
  stb: 'STB',
  ohb: 'OHB',
  lifter: 'Lifter',
  'support-pillar': 'Pillar',
  'horizontal-beam': 'Beam',
  'suspension-rod': 'Rod',
  rectangle: 'Area',
  circle: 'Zone',
  text: 'Label',
};

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Next free `<Prefix><n>` name for a type.
 *
 * `reserved` holds normalized names already handed out in the current batch but
 * not yet in the store — without it, two elements created in one commit (e.g.
 * the two halves of a rail split by a junction) would both get the same name.
 */
export function computeNextEntityName(
  type: ElementType,
  elements: Iterable<LayoutElement>,
  reserved: ReadonlySet<string> = new Set()
): string {
  const prefix = ENTITY_NAME_PREFIX[type] ?? 'Item';
  const pattern = new RegExp(`^${escapeRegex(prefix)}\\s*(\\d+)$`, 'i');
  const taken = new Set<string>();
  let maxIndex = 0;
  let typeCount = 0;

  for (const element of elements) {
    const name = (element.metadata.name || '').trim();
    if (name) taken.add(normalize(name));
    if (element.type !== type) continue;
    typeCount += 1;
    const match = name.match(pattern);
    if (!match) continue;
    const index = Number(match[1]);
    if (Number.isFinite(index)) maxIndex = Math.max(maxIndex, index);
  }

  let index = maxIndex > 0 ? maxIndex + 1 : typeCount + 1;
  let candidate = `${prefix}${index}`;
  while (taken.has(normalize(candidate)) || reserved.has(normalize(candidate))) {
    index += 1;
    candidate = `${prefix}${index}`;
  }
  return candidate;
}

/**
 * Name factory that reserves every name it issues, so a single user action that
 * creates several elements of one type never produces duplicates.
 */
export function createEntityNameAllocator(
  elements: Iterable<LayoutElement>
): (type: ElementType) => string {
  const snapshot = Array.from(elements);
  const reserved = new Set<string>();

  return (type: ElementType) => {
    const name = computeNextEntityName(type, snapshot, reserved);
    reserved.add(normalize(name));
    return name;
  };
}
